/**
 * CoachPlaybook - Auth Client
 * Version: 0.7.0
 * Description: Client-side Google OAuth utilities with token management and auto-refresh
 */

const API_BASE = process.env.REACT_APP_API_URL || 'https://coachplaybook-api.pandorasbox.workers.dev';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const REDIRECT_URI = process.env.REACT_APP_GOOGLE_REDIRECT_URI || `${window.location.origin}/auth/callback`;

// Storage keys
const TOKEN_KEY = 'cp_token';
const REFRESH_KEY = 'cp_refresh';
const USER_KEY = 'cp_user';
const DEVICE_KEY = 'cp_device_id';

// Get or create persistent device ID
export const getDeviceId = () => {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
};

// Device label from user agent
export const getDeviceLabel = () => {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return 'Browser';
};

// Generate Google OAuth URL
export const getGoogleAuthUrl = () => {
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    scope: 'openid email profile',
    access_type: 'offline',
    prompt: 'consent'
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
};

// Exchange Google code for app tokens
export const exchangeCode = async (code) => {
  const res = await fetch(`${API_BASE}/api/v1/auth/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code,
      redirect_uri: REDIRECT_URI,
      device_id: getDeviceId(),
      device_label: getDeviceLabel()
    })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Auth failed' }));
    throw new Error(err.error || `Auth failed: ${res.status}`);
  }
  const data = await res.json();
  setTokens(data.token, data.refresh_token);
  setStoredUser(data.user);
  return data.user;
};

// Token management
export const setTokens = (access, refresh) => {
  localStorage.setItem(TOKEN_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
};

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);

export const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
};

// User management
export const setStoredUser = (user) => localStorage.setItem(USER_KEY, JSON.stringify(user));
export const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
};

// Refresh token flow
export const refreshTokens = async () => {
  const refresh = getRefreshToken();
  if (!refresh) throw new Error('No refresh token');
  const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refresh, device_id: getDeviceId() })
  });
  if (!res.ok) throw new Error('Refresh failed');
  const data = await res.json();
  setTokens(data.token, data.refresh_token);
  return data.token;
};

// Authenticated fetch with auto-refresh
export const authFetch = async (url, options = {}) => {
  let token = getToken();
  const makeRequest = (t) => fetch(`${API_BASE}${url}`, {
    ...options,
    headers: { ...options.headers, Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' }
  });
  let res = await makeRequest(token);
  if (res.status === 401) {
    try {
      token = await refreshTokens();
      res = await makeRequest(token);
    } catch {
      clearTokens();
      throw new Error('Session expired');
    }
  }
  return res;
};

// Logout
export const logout = () => {
  const token = getToken();
  if (token) {
    fetch(`${API_BASE}/api/v1/auth/session`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    }).catch(() => {});
  }
  clearTokens();
};

// Check if logged in
export const isLoggedIn = () => !!getToken() && !!getStoredUser();

// Get tier limits
export const getTierLimits = async () => {
  const res = await authFetch('/api/v1/tier/limits');
  if (!res.ok) return null;
  return res.json();
};
